import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useEffectOnce, useToggle } from 'react-use';
import { firmSettingsAccessSelector } from '../../../AdviserArea/AdviserAreaSelector';
import ErrorBubble from '../../../CommonComponents/ErrorBubble';
import EditCMAModal from '../../../CommonComponents/Modal';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { ButtonGroup } from '../../../CommonComponents/OPL/ButtonGroup/ButtonGroup';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { Shimmer } from '../../../CommonComponents/OPL/Shimmer/Shimmer';

import { MARKET_DATA_ASSUMPTIONS_FETCH } from './actions';
import {
  CapitalMarketAssumptionsTable,
  LoadingCapitalMarketAssumptionsTable,
} from './components/CapitalMarketAssumptionsTable';
import { CorrelationMatrix, LoadingCorrelationMatrix } from './components/CorrelationMatrix';
import EditCustomModelAssumptions from './components/EditCustomModelAssumptions';
import { Modal, ModalProps } from './components/Modal/Modal';
import { DROPDOWN_OPTIONS, MODAL_TEXT } from './constants';
import {
  selectAssetClassesIndexedById,
  selectCmaType,
  selectCorrelations,
  selectIncludedAssetClasses,
  selectReturns,
  selectVolatilities,
} from './selector';
import { Explanations, Page, Panel, StyledTitle as Title } from './styles';
import { CMAType, ModalType } from './types';
import useApi from '../../../_hooks/useApi';

interface ModalOptions extends Omit<ModalProps, 'isOpen'> {}

const CustomModelAssumptions: React.FC = () => {
  const [editModalOpen, toggleEditModal] = useToggle(false);
  const [modalDetails, setModalDetails] = useState<ModalOptions | null>(null);

  const assetClassesIndexedById = useSelector(selectAssetClassesIndexedById);
  const includedAssetClasses = useSelector(selectIncludedAssetClasses);
  const correlations = useSelector(selectCorrelations);
  const returns = useSelector(selectReturns);
  const volatilities = useSelector(selectVolatilities);

  const cmaType = useSelector(selectCmaType);

  const hasAccessToFirmSettings = useSelector(firmSettingsAccessSelector);

  const [fetchMarketDataAssumptions, { loading, errors }] = useApi(
    '/Adviser/MarketDataAssumptions/GetMarketDataAssumptions',
    {
      reduxActionPrefix: MARKET_DATA_ASSUMPTIONS_FETCH,
    },
  );

  const [resetMarketDataAssumptions, { loading: loadingReset, errors: resetErrors }] = useApi(
    '/Adviser/MarketDataAssumptions/ResetMarketDataAssumptions',
    {
      method: 'POST',
    },
  );

  const [setCMAs, { errors: setCMAsErrors }] = useApi(
    '/Adviser/MarketDataAssumptions/SetCapitalMarketAssumptions',
    {
      method: 'POST',
      bodyEncoding: 'formData',
    },
  );

  useEffectOnce(() => {
    fetchMarketDataAssumptions();
  });

  return (
    <Page>
      <Box mb={16}>
        {loading ? (
          <>
            <Box display="flex" justifyContent="flex-start" mb={4}>
              <Shimmer width={150} height={16} />
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Shimmer width={300} height={36} />
              <Box display="flex">
                <Shimmer width={100} height={36} mr={8} />
                <Shimmer width={100} height={36} />
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box width="310px">
                {hasAccessToFirmSettings && (
                  <Select
                    label="CMA Selection"
                    options={DROPDOWN_OPTIONS}
                    value={DROPDOWN_OPTIONS.find(({ value }) => value === cmaType)}
                    onChange={target => {
                      if (target?.value === CMAType.CustomCMAs) {
                        toggleEditModal();
                      } else {
                        setModalDetails({
                          ...MODAL_TEXT[ModalType.CMACHANGE],
                          onCancel: () => setModalDetails(null),
                          onSuccess: async () => {
                            await setCMAs({
                              body: { cmaType: target?.value || 0 },
                            });
                            setModalDetails(null);
                            fetchMarketDataAssumptions();
                          },
                        });
                      }
                    }}
                  />
                )}
              </Box>
              <ButtonGroup>
                {cmaType === CMAType.Year2023CMAs && (
                  <Button variant="subtleActive" leftIcon="fas fa-cog" onClick={toggleEditModal}>
                    Edit
                  </Button>
                )}
                <Button
                  disabled={loadingReset}
                  isLoading={loadingReset}
                  variant="subtleActive"
                  leftIcon="fas fa-redo"
                  onClick={() => {
                    setModalDetails({
                      ...MODAL_TEXT[ModalType.RESET],
                      onCancel: () => setModalDetails(null),
                      onSuccess: async () => {
                        resetMarketDataAssumptions({
                          onSuccess() {
                            setModalDetails(null);
                            fetchMarketDataAssumptions();
                          },
                          onError() {
                            setModalDetails(null);
                          },
                        });
                      },
                    });
                  }}
                >
                  Reset
                </Button>
              </ButtonGroup>
            </Box>
          </>
        )}
      </Box>

      {[...resetErrors, ...setCMAsErrors].length > 0 && (
        <Box mb={16}>
          <Alert message={[...resetErrors, ...setCMAsErrors].toString()} variant="error" />
        </Box>
      )}

      <EditCMAModal
        isOpen={editModalOpen}
        onRequestClose={toggleEditModal}
        showCloseModalCross
        shouldCloseOnOverlayClick={false}
      >
        <EditCustomModelAssumptions
          onFinish={() => {
            toggleEditModal();

            fetchMarketDataAssumptions();
          }}
        />
      </EditCMAModal>

      <Modal isOpen={!!modalDetails} {...modalDetails} />

      <ErrorBubble show={errors.length > 0} errors={errors} />

      <Panel>
        <Title>Capital Market Assumptions</Title>

        {loading ? (
          <LoadingCapitalMarketAssumptionsTable />
        ) : (
          <CapitalMarketAssumptionsTable
            includedAssetClasses={includedAssetClasses}
            returns={returns}
            volatilities={volatilities}
          />
        )}
      </Panel>

      <Explanations>
        <div>
          <Title>Standard Deviation</Title>
          <p>
            Standard deviation is used by investors to gauge the level of volatility in an
            investment. A risky investment will have a higher standard deviation, whereas a less
            risky investment will have a lower standard deviation.
          </p>
        </div>
        <div>
          <Title>Projected Return</Title>
          <p>
            The projected return is calculated by taking a weighted average of each asset class and
            applying their respective average return using forward looking capital market
            assumptions.
          </p>
        </div>
      </Explanations>

      <Panel>
        <Title>Correlation Matrix</Title>
        {loading ? (
          <LoadingCorrelationMatrix />
        ) : (
          <CorrelationMatrix
            assetClassesIndexedById={assetClassesIndexedById}
            correlations={correlations}
          />
        )}
      </Panel>
    </Page>
  );
};

export default CustomModelAssumptions;
